import {
  handleAddLocations,
  handleAddMachines,
  handleAddSpecificLocations,
  handleLocationSelect,
  handleMachineSelect,
  handleMachineSelectAlt,
  handleMachineSelectWithSpecLocationAlt,
  handleSelectAll,
  handleSpecificLocationSelect,
} from '../components/store/slices/masterControlSelectSlice';

// Clicking a row toggles it: every level writes the flipped value instead of a
// hardcoded `true`, which is why a row could never be turned back off before.
// Deselecting a parent clears everything under it, and deselecting a child
// clears the parents above it, because the apply step reads a lit parent as
// "take all of its switches".

// isMachineSelected[location] is either a flat list of booleans or, when the
// location has sub locations, a list of lists.
const setAllMachines = (locationMachines, value) =>
  locationMachines?.map((el) =>
    Array.isArray(el) ? el.map(() => value) : value,
  );

const withValue = (list, value, isSelected) =>
  isSelected
    ? list.includes(value)
      ? [...list]
      : [...list, value]
    : list.filter((el) => el !== value);

const sameMachine = (entry, target) =>
  entry.length === target.length && entry.every((el, i) => el === target[i]);

// When a child is switched off its parent stops being lit, so the parent's
// coarse "whole location" entry has to be replaced by an explicit entry per
// switch that is still selected underneath it.
const remainingMachineDescriptors = (locationKey, location, locationMachines) => {
  const list = [];

  if (location.isSpecificLocation) {
    Object.entries(location.subLocations).forEach(
      ([subKey, subLocation], subIdx) => {
        Object.keys(subLocation.devices).forEach((device, deviceIdx) => {
          if (locationMachines?.[subIdx]?.[deviceIdx]) {
            list.push({
              location: locationKey,
              specificLocation: subKey,
              device,
            });
          }
        });
      },
    );
  } else {
    Object.keys(location.devices).forEach((device, deviceIdx) => {
      if (locationMachines?.[deviceIdx]) {
        list.push({ location: locationKey, specificLocation: null, device });
      }
    });
  }

  return list;
};

// `selectedMachines` holds the entries the apply step replays, in the same
// shape the select handlers push: [location, device] or, under a sub location,
// [location, specificLocation, device].
const toMachineEntry = ({ location, specificLocation, device }) =>
  specificLocation ? [location, specificLocation, device] : [location, device];

const mergeMachineEntries = (list, descriptors) =>
  descriptors.reduce((result, descriptor) => {
    const entry = toMachineEntry(descriptor);
    return result.some((el) => sameMachine(el, entry))
      ? result
      : [...result, entry];
  }, [...list]);

// A descriptor removes any entry it covers: clearing a location also clears the
// individual switches queued under it.
export const coversSelection = (target, entry) =>
  entry.location === target.location &&
  (target.specificLocation == null ||
    entry.specificLocation === target.specificLocation) &&
  (target.device == null || entry.device === target.device);

export const useSelectSwitchesDisplay = (
  option,
  machine,
  extraOption,
  machineIndex,
  specificLocations,
  swt,
  data,
  allSelectBoxData,
  dispatch,
) => {
  const locations = Object.keys(data);
  const {
    isAllSelected,
    isLocationSelected,
    isSpecificLocationSelected,
    isMachineSelected,
    selectedSpecificLocations,
    selectedMachines,
    selectedLocations,
  } = allSelectBoxData;
  const selectedList = [];
  let expanded = [];
  let select;

  if (option === 'all') {
    // 1. toggle all
    select = !isAllSelected;

    dispatch(handleSelectAll({ switch: swt, status: select }));

    // 2.update all locations
    const locationArr = isLocationSelected.map(() => select);

    dispatch(handleLocationSelect({ switch: swt, arr: locationArr }));

    // 3.update all isSpecificLocationSelected
    const specificLocationArr = isSpecificLocationSelected.map((el) =>
      el.map(() => select),
    );

    dispatch(
      handleSpecificLocationSelect({ switch: swt, arr: specificLocationArr }),
    );

    // 4.update all machines
    const individualArr = Object.values(data)?.map((location) => {
      if (location.isSpecificLocation) {
        return Object.values(location.subLocations).map((subLocation) => {
          return Object.values(subLocation.devices).map(() => select);
        });
      } else {
        return Object.values(location.devices).map(() => select);
      }
    });

    dispatch(handleMachineSelect({ switch: swt, arr: individualArr }));

    if (!select) {
      // nothing is lit anymore, so the pending lists have to go too or apply
      // would re-expand locations the user just cleared
      dispatch(handleAddLocations({ switch: swt, arr: [] }));
      dispatch(handleAddSpecificLocations({ switch: swt, arr: [] }));
      dispatch(handleAddMachines({ switch: swt, arr: [] }));
    }

    Object.keys(data).forEach((locKey) => {
      selectedList.push({
        location: locKey,
        specificLocation: null,
        device: null,
      });
    });
  } else if (option !== 'all' && machine === undefined) {
    // 2. toggle location
    // 2.1 update  isLocationSelected
    const index = locations.indexOf(option);
    select = !isLocationSelected[index];

    const arr = [...isLocationSelected];
    arr[index] = select;
    dispatch(handleLocationSelect({ switch: swt, arr }));

    if (!select) {
      dispatch(handleSelectAll({ switch: swt, status: false }));
    }

    // 2.2 update isSpecificLocationSelected
    const exitingSpecLocationsValue = Object.values(data).filter(
      (el) => el.isSpecificLocation,
    );

    const newIsSpecificLocationSelected = [];
    exitingSpecLocationsValue.forEach((specLocationTitle) => {
      const tempArr = [];

      newIsSpecificLocationSelected.push(tempArr);

      if (data[option].subLocations) {
        Object.keys(data[option].subLocations).forEach((el, index) => {
          if (el === Object.keys(specLocationTitle.subLocations)[index]) {
            tempArr.push(select);
          } else {
            tempArr.push(false);
          }
        });
      }
    });

    dispatch(
      handleSpecificLocationSelect({
        switch: swt,
        arr: newIsSpecificLocationSelected,
      }),
    );

    // 2.3 update isMachineSelected
    const machineNewArr = setAllMachines(isMachineSelected[index], select);
    const copyArr = [...isMachineSelected];
    copyArr[index] = machineNewArr;
    dispatch(handleMachineSelect({ switch: swt, arr: copyArr }));

    // 2.4 for dispatch selected locations
    dispatch(
      handleAddLocations({
        switch: swt,
        arr: withValue(selectedLocations, option, select),
      }),
    );

    if (!select) {
      // drop everything this location had queued up
      const subKeys = Object.keys(data[option].subLocations || {});
      dispatch(
        handleAddSpecificLocations({
          switch: swt,
          arr: selectedSpecificLocations.filter((el) => !subKeys.includes(el)),
        }),
      );
      dispatch(
        handleAddMachines({
          switch: swt,
          arr: selectedMachines.filter((el) => el[0] !== option),
        }),
      );
    }

    selectedList.push({
      location: option,
      specificLocation: null,
      device: null,
    });
  } else if (option !== 'all' && machine === 'isSpecificLocation') {
    // 3. toggle specific location
    // 3.1 update isSpecificLocationSelected
    let index;
    specificLocations.forEach((el, i) => {
      if (el.includes(extraOption)) {
        index = i;
      }
    });

    select = !isSpecificLocationSelected[index]?.[machineIndex];

    const isSpecificLocationNewArr = isSpecificLocationSelected[index]?.map(
      (_, idx) => {
        if (idx === machineIndex) {
          return select;
        }
        return _;
      },
    );
    const arr = [...isSpecificLocationSelected];

    arr[index] = isSpecificLocationNewArr;

    dispatch(handleSpecificLocationSelect({ switch: swt, arr }));

    // 3.2 update isMachineSelected
    const locationIndex = locations.indexOf(option);
    const machineNewArr = isMachineSelected[locationIndex][machineIndex]?.map(
      () => select,
    );

    const deepCopyArr = JSON.parse(JSON.stringify(isMachineSelected));

    deepCopyArr[locationIndex][machineIndex] = machineNewArr;
    dispatch(handleMachineSelect({ switch: swt, arr: deepCopyArr }));

    if (!select) {
      // the parent location is no longer fully selected
      dispatch(handleSelectAll({ switch: swt, status: false }));

      const locationArr = [...isLocationSelected];
      locationArr[locationIndex] = false;
      dispatch(handleLocationSelect({ switch: swt, arr: locationArr }));

      dispatch(
        handleAddLocations({
          switch: swt,
          arr: selectedLocations.filter((el) => el !== option),
        }),
      );
      // the location entry goes with it, so re-state whatever is still lit
      selectedList.push({
        location: option,
        specificLocation: null,
        device: null,
      });
      expanded = remainingMachineDescriptors(
        option,
        data[option],
        deepCopyArr[locationIndex],
      );

      dispatch(
        handleAddMachines({
          switch: swt,
          arr: mergeMachineEntries(
            selectedMachines.filter((el) => el[1] !== extraOption),
            expanded,
          ),
        }),
      );
    }

    // for dispatch selected specific locations
    dispatch(
      handleAddSpecificLocations({
        switch: swt,
        arr: withValue(selectedSpecificLocations, extraOption, select),
      }),
    );

    selectedList.push({
      location: option,
      specificLocation: extraOption,
      device: null,
    });
  } else {
    // 4. toggle an individual machine

    const locationIdx = Object.keys(data).indexOf(option);
    let newSelectedMachineArr;
    let specLocationIdx;
    let machineIdx;

    // 4.1 update isMachineSelected that has a specific location
    if (extraOption) {
      specLocationIdx = Object.keys(data[option].subLocations).indexOf(
        extraOption,
      );
      machineIdx = Object.keys(
        data[option].subLocations[extraOption].devices,
      ).indexOf(machine);

      select =
        !isMachineSelected[locationIdx]?.[specLocationIdx]?.[machineIdx];

      dispatch(
        handleMachineSelectWithSpecLocationAlt({
          switch: swt,
          locationIdx,
          specLocationIdx,
          machineIdx,
          status: select,
        }),
      );

      newSelectedMachineArr = select
        ? [...selectedMachines, [option, extraOption, machine]]
        : selectedMachines.filter(
            (el) => !sameMachine(el, [option, extraOption, machine]),
          );

      selectedList.push({
        location: option,
        specificLocation: extraOption,
        device: machine,
      });
    } else {
      // 4.2 update isMachineSelected that doesn't have a specific location
      machineIdx = Object.keys(data[option].devices).indexOf(machine);

      select = !isMachineSelected[locationIdx]?.[machineIdx];

      dispatch(
        handleMachineSelectAlt({
          switch: swt,
          locationIdx,
          machineIdx,
          status: select,
        }),
      );

      newSelectedMachineArr = select
        ? [...selectedMachines, [option, machine]]
        : selectedMachines.filter(
            (el) => !sameMachine(el, [option, machine]),
          );

      selectedList.push({
        location: option,
        specificLocation: null,
        device: machine,
      });
    }

    if (!select) {
      // a parent that is no longer complete must not stay lit, otherwise apply
      // would expand it back to every machine including this one
      dispatch(handleSelectAll({ switch: swt, status: false }));

      const locationArr = [...isLocationSelected];
      locationArr[locationIdx] = false;
      dispatch(handleLocationSelect({ switch: swt, arr: locationArr }));

      dispatch(
        handleAddLocations({
          switch: swt,
          arr: selectedLocations.filter((el) => el !== option),
        }),
      );

      // the parent entries are dropped, so re-state the switches still lit
      const nextLocationMachines = JSON.parse(
        JSON.stringify(isMachineSelected[locationIdx] ?? []),
      );
      if (extraOption) {
        nextLocationMachines[specLocationIdx][machineIdx] = select;
      } else {
        nextLocationMachines[machineIdx] = select;
      }

      selectedList.push({
        location: option,
        specificLocation: null,
        device: null,
      });
      expanded = remainingMachineDescriptors(
        option,
        data[option],
        nextLocationMachines,
      );
      newSelectedMachineArr = mergeMachineEntries(
        newSelectedMachineArr,
        expanded,
      );

      if (extraOption) {
        let specIdx;
        specificLocations.forEach((el, i) => {
          if (el.includes(extraOption)) {
            specIdx = i;
          }
        });

        if (specIdx !== undefined && isSpecificLocationSelected[specIdx]) {
          const specArr = JSON.parse(
            JSON.stringify(isSpecificLocationSelected),
          );
          specArr[specIdx][specLocationIdx] = false;
          dispatch(handleSpecificLocationSelect({ switch: swt, arr: specArr }));
        }

        dispatch(
          handleAddSpecificLocations({
            switch: swt,
            arr: selectedSpecificLocations.filter((el) => el !== extraOption),
          }),
        );
      }
    }

    // for dispatch selected machines
    dispatch(handleAddMachines({ switch: swt, arr: newSelectedMachineArr }));
  }

  return { selectedList, select, expanded };
};
